export interface CategoryMeta {
  icon: string;
  color: string;
  bgGradient: string;
  description: string;
}

interface CategoryStoreShape {
  category: string;
  subcategory?: string;
  isActive: boolean;
  promos?: Array<{ isActive: boolean }>;
}

export interface CategoryAggregate<TStore> {
  id: string;
  name: string;
  stores: TStore[];
  promosCount: number;
  subcategories: string[];
}

const CYRILLIC_TO_LATIN: Record<string, string> = {
  'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'ё': 'yo',
  'ж': 'zh', 'з': 'z', 'и': 'i', 'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm',
  'н': 'n', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 'у': 'u',
  'ф': 'f', 'х': 'h', 'ц': 'ts', 'ч': 'ch', 'ш': 'sh', 'щ': 'sch', 'ъ': '',
  'ы': 'y', 'ь': '', 'э': 'e', 'ю': 'yu', 'я': 'ya',
};

const CATEGORY_ALIASES: Record<string, string> = {
  'Apps': 'Онлайн-сервисы',
  'Автомобили': 'Авто',
  'Business & Services': 'Услуги',
  'Consumer Electronics': 'Электроника',
  'E-commerce & Retail': 'Маркетплейсы',
  'Finance & Insurance': 'Финансы и страхование',
  'Gaming': 'Игры и развлечения',
  'Mobile Services & Telecommunications': 'Связь',
  'Software': 'Онлайн-сервисы',
  'Software & Mobile Apps': 'Онлайн-сервисы',
  "Women's Apparel": 'Одежда и обувь',
  'Возможности для бизнеса': 'Услуги',
  'Для дома и сада': 'Дом и сад',
  'Дети': 'Детские товары',
  'Дом': 'Дом и сад',
  'Здоровье и красота': 'Красота и здоровье',
  'Игры': 'Игры и развлечения',
  'Компьютеры и интернет': 'Онлайн-сервисы',
  'Кухонные принадлежности и бытовая техника': 'Дом и сад',
  'Красота': 'Красота и здоровье',
  'Подарочные сертификаты и карты': 'Подарки и сертификаты',
  'Продукты для офиса': 'Товары для офиса',
  'Украшения и аксессуары': 'Украшения и аксессуары',
  'Украшения и аксессуары ': 'Украшения и аксессуары',
  'Потребительские товары': 'Маркетплейсы',
  'Финансы и страхование': 'Финансы и страхование',
  'Музыка и видео': 'Игры и развлечения',
  'Онлайн Сервисы': 'Онлайн-сервисы',
};

const DEFAULT_CATEGORY_META: CategoryMeta = {
  icon: 'ri-apps-line',
  color: 'bg-gray-100 text-gray-600',
  bgGradient: 'from-gray-500 to-gray-600',
  description: 'Лучшие промокоды и скидки',
};

const CATEGORY_META: Record<string, CategoryMeta> = {
  'Авто': {
    icon: 'ri-car-line',
    color: 'bg-gray-100 text-gray-600',
    bgGradient: 'from-gray-500 to-slate-600',
    description: 'Автотовары, сервис и полезные предложения для автомобилистов',
  },
  'Детские товары': {
    icon: 'ri-bear-smile-line',
    color: 'bg-pink-100 text-pink-600',
    bgGradient: 'from-pink-500 to-fuchsia-600',
    description: 'Игрушки, одежда, курсы и полезные товары для детей',
  },
  'Дом и сад': {
    icon: 'ri-home-line',
    color: 'bg-green-100 text-green-600',
    bgGradient: 'from-green-500 to-emerald-600',
    description: 'Мебель, декор, техника и товары для дома и сада по выгодным ценам',
  },
  'Еда и напитки': {
    icon: 'ri-restaurant-line',
    color: 'bg-red-100 text-red-600',
    bgGradient: 'from-red-500 to-orange-600',
    description: 'Доставка еды, продукты и гастрономические предложения со скидками',
  },
  'Игры и развлечения': {
    icon: 'ri-gamepad-line',
    color: 'bg-purple-100 text-purple-600',
    bgGradient: 'from-purple-500 to-pink-600',
    description: 'Игры, билеты, стриминг и развлечения с промокодами и акциями',
  },
  'Другое': {
    icon: 'ri-apps-2-line',
    color: 'bg-slate-100 text-slate-600',
    bgGradient: 'from-slate-500 to-gray-600',
    description: 'Разные полезные акции, сервисы и специальные предложения',
  },
  'Книги': {
    icon: 'ri-book-2-line',
    color: 'bg-amber-100 text-amber-700',
    bgGradient: 'from-amber-500 to-orange-600',
    description: 'Книги, аудиокниги и литературные сервисы с выгодными акциями',
  },
  'Маркетплейсы': {
    icon: 'ri-store-2-line',
    color: 'bg-slate-100 text-slate-700',
    bgGradient: 'from-slate-500 to-zinc-600',
    description: 'Маркетплейсы и крупные ритейлеры с ежедневными акциями и промокодами',
  },
  'Красота и здоровье': {
    icon: 'ri-heart-line',
    color: 'bg-fuchsia-100 text-fuchsia-600',
    bgGradient: 'from-fuchsia-500 to-pink-600',
    description: 'Косметика, уход, аптеки и товары для здоровья со скидками',
  },
  'Образование': {
    icon: 'ri-book-line',
    color: 'bg-indigo-100 text-indigo-600',
    bgGradient: 'from-indigo-500 to-blue-600',
    description: 'Онлайн-курсы, обучение, книги и полезные сервисы для развития',
  },
  'Одежда и обувь': {
    icon: 'ri-shirt-line',
    color: 'bg-pink-100 text-pink-600',
    bgGradient: 'from-pink-500 to-rose-600',
    description: 'Модная одежда, обувь и аксессуары со скидками и промокодами',
  },
  'Онлайн-сервисы': {
    icon: 'ri-global-line',
    color: 'bg-violet-100 text-violet-600',
    bgGradient: 'from-violet-500 to-purple-600',
    description: 'Подписки, digital-сервисы и интернет-продукты по выгодным ценам',
  },
  'Подарки и сертификаты': {
    icon: 'ri-gift-2-line',
    color: 'bg-pink-100 text-pink-600',
    bgGradient: 'from-pink-500 to-rose-600',
    description: 'Подарочные карты, сертификаты и впечатления по выгодным ценам',
  },
  'Путешествия': {
    icon: 'ri-plane-line',
    color: 'bg-orange-100 text-orange-600',
    bgGradient: 'from-orange-500 to-amber-600',
    description: 'Авиабилеты, туры, отели и трансферы с акциями и промокодами',
  },
  'Связь': {
    icon: 'ri-phone-line',
    color: 'bg-cyan-100 text-cyan-600',
    bgGradient: 'from-cyan-500 to-blue-600',
    description: 'Мобильная связь, eSIM и интернет-сервисы со скидками',
  },
  'Товары для животных': {
    icon: 'ri-bear-smile-line',
    color: 'bg-amber-100 text-amber-600',
    bgGradient: 'from-amber-500 to-orange-600',
    description: 'Корма, аксессуары и товары для питомцев по специальным ценам',
  },
  'Товары для офиса': {
    icon: 'ri-briefcase-4-line',
    color: 'bg-stone-100 text-stone-700',
    bgGradient: 'from-stone-500 to-neutral-600',
    description: 'Офисные товары, расходники и решения для работы по специальным ценам',
  },
  'Услуги': {
    icon: 'ri-service-line',
    color: 'bg-teal-100 text-teal-600',
    bgGradient: 'from-teal-500 to-cyan-600',
    description: 'Полезные онлайн- и офлайн-услуги со скидками и выгодными предложениями',
  },
  'Украшения и аксессуары': {
    icon: 'ri-gemini-line',
    color: 'bg-rose-100 text-rose-600',
    bgGradient: 'from-rose-500 to-pink-600',
    description: 'Украшения, аксессуары и стильные детали со скидками и акциями',
  },
  'Электроника': {
    icon: 'ri-smartphone-line',
    color: 'bg-sky-100 text-sky-600',
    bgGradient: 'from-sky-500 to-blue-600',
    description: 'Смартфоны, техника и гаджеты с промокодами и скидками',
  },
  'Финансы и страхование': {
    icon: 'ri-money-dollar-circle-line',
    color: 'bg-emerald-100 text-emerald-600',
    bgGradient: 'from-emerald-500 to-green-600',
    description: 'Финансовые сервисы, страхование и банковские продукты с бонусами',
  },
};

export function normalizeCategoryName(value: string): string {
  const normalized = value.trim();
  return CATEGORY_ALIASES[normalized] ?? normalized;
}

export function slugifyPathSegment(value: string): string {
  return normalizeCategoryName(value)
    .toLowerCase()
    .replace(/[а-яё]/g, (char) => CYRILLIC_TO_LATIN[char] ?? char)
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function buildCategoryId(value: string): string {
  return slugifyPathSegment(value);
}

export function buildSubcategoryId(value: string): string {
  return slugifyPathSegment(value);
}

export function getCategoryRoute(value: string): string {
  return `/category/${buildCategoryId(value)}`;
}

export function getCategoryMeta(value: string): CategoryMeta {
  const normalized = normalizeCategoryName(value);
  return CATEGORY_META[normalized] ?? DEFAULT_CATEGORY_META;
}

export function buildCategoryCollection<TStore extends CategoryStoreShape>(stores: TStore[]): CategoryAggregate<TStore>[] {
  const categoryMap = new Map<string, { stores: TStore[]; promosCount: number; subcategories: Set<string> }>();

  for (const store of stores) {
    if (!store.isActive) {
      continue;
    }

    const normalizedCategory = normalizeCategoryName(store.category);
    const current = categoryMap.get(normalizedCategory) ?? {
      stores: [],
      promosCount: 0,
      subcategories: new Set<string>(),
    };

    current.stores.push(store);
    current.promosCount += (store.promos ?? []).filter((promo) => promo.isActive).length;

    if (store.subcategory?.trim()) {
      current.subcategories.add(store.subcategory.trim());
    }

    categoryMap.set(normalizedCategory, current);
  }

  return Array.from(categoryMap.entries())
    .map(([name, data]) => ({
      id: buildCategoryId(name),
      name,
      stores: data.stores,
      promosCount: data.promosCount,
      subcategories: Array.from(data.subcategories).sort((left, right) => left.localeCompare(right, 'ru')),
    }))
    .sort((left, right) => left.name.localeCompare(right.name, 'ru'));
}
